import { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, Check, X } from 'lucide-react';
import './Select.css';

export default function Select({
    name,
    label,
    options = [],
    value,
    onChange,
    placeholder = 'Select...',
    searchable = false,
    clearable = false,
    disabled = false,
    error,
    required = false,
    fullWidth = true,
    className = ''
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const inputRef = useRef(null);

    const selectedOption = options.find(opt => opt.value === value);

    const filteredOptions = searchable
        ? options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()))
        : options;

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleSelect = (option) => {
        // Create event-like object for consistent form handling
        const event = { target: { name, value: option.value } };
        onChange?.(event);
        setIsOpen(false);
        setSearch('');
    };

    const handleClear = (e) => {
        e.stopPropagation();
        const event = { target: { name, value: '' } };
        onChange?.(event);
    };

    return (
        <div className={`select-wrapper ${fullWidth ? 'select-full' : ''} ${className}`} ref={containerRef}>
            {label && (
                <label className="select-label">
                    {label}
                    {required && <span className="select-required">*</span>}
                </label>
            )}
            <div
                className={`select-control ${isOpen ? 'open' : ''} ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
                onClick={() => !disabled && setIsOpen(!isOpen)}
            >
                {searchable && isOpen ? (
                    <input
                        ref={inputRef}
                        type="text"
                        className="select-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={selectedOption?.label || placeholder}
                        autoFocus
                    />
                ) : (
                    <span className={`select-value ${!selectedOption ? 'placeholder' : ''}`}>
                        {selectedOption?.label || placeholder}
                    </span>
                )}
                <div className="select-actions">
                    {clearable && selectedOption && (
                        <X size={16} className="select-clear" onClick={handleClear} />
                    )}
                    <ChevronDown size={18} className={`select-arrow ${isOpen ? 'rotate' : ''}`} />
                </div>
            </div>
            {isOpen && (
                <div className="select-dropdown">
                    {filteredOptions.length === 0 ? (
                        <div className="select-empty">No options found</div>
                    ) : (
                        filteredOptions.map((option) => (
                            <div
                                key={option.value}
                                className={`select-option ${option.value === value ? 'selected' : ''}`}
                                onClick={() => handleSelect(option)}
                            >
                                <span>{option.label}</span>
                                {option.value === value && <Check size={16} />}
                            </div>
                        ))
                    )}
                </div>
            )}
            {error && <span className="select-error">{error}</span>}
        </div>
    );
}

Select.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        label: PropTypes.string.isRequired
    })),
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func,
    placeholder: PropTypes.string,
    searchable: PropTypes.bool,
    clearable: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.string,
    required: PropTypes.bool,
    fullWidth: PropTypes.bool,
    className: PropTypes.string
};
